import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Card, CardContent } from "@/components/ui/card";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { supabase } from "@/integrations/supabase/client";

interface GuideSummary {
  slug: string;
  kind: string;
  title: string;
  description: string;
  published_at: string;
}

const SECTIONS: { kind: string; label: string; blurb: string }[] = [
  { kind: "holiday", label: "Holiday & peak travel", blurb: "What to expect at the busiest airports around each holiday rush." },
  { kind: "monthly", label: "Monthly wait reports", blurb: "What we actually measured at the security checkpoints we track." },
  { kind: "compare", label: "Airport comparisons", blurb: "Two airports, side by side, when you can pick either one." },
  { kind: "airline", label: "Airline guides", blurb: "Your terminal, your security queue, how long to allow." },
];

const GuidesIndex = () => {
  const [guides, setGuides] = useState<GuideSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from("content_pages")
      .select("slug, kind, title, description, published_at")
      .eq("status", "published")
      .order("published_at", { ascending: false })
      .limit(500)
      .then(({ data }) => {
        setGuides((data as GuideSummary[]) ?? []);
        setLoading(false);
      });
  }, []);

  return (
    <>
      <Helmet>
        <title>Airport Guides – Waits, Terminals & Holiday Travel</title>
        <meta
          name="description"
          content="Practical airport guides from Airport Buddy: holiday security waits, monthly wait reports, airport comparisons and airline terminal guides."
        />
        <link rel="canonical" href="https://airportbuddy.app/guides" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Airport Guides – Airport Buddy" />
        <meta property="og:description" content="Holiday waits, monthly wait reports, airport comparisons and airline terminal guides." />
        <meta property="og:url" content="https://airportbuddy.app/guides" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-background via-secondary/5 to-accent/10">
        <div className="container mx-auto px-4 py-8">
          <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Guides", href: "/guides" }]} />

          <div className="max-w-3xl mx-auto space-y-8">
            <header className="space-y-3">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground">Airport guides</h1>
              <p className="text-base text-muted-foreground">
                Short, practical guides built from the security wait readings we collect and from published
                airport information. New ones appear as the travel calendar moves.
              </p>
            </header>

            {loading && <p className="text-base text-muted-foreground">Loading guides…</p>}

            {!loading && guides.length === 0 && (
              <Card className="rounded-xl">
                <CardContent className="pt-6 text-base text-muted-foreground">
                  The first guides are being written now — check back shortly. In the meantime,{" "}
                  <Link to="/airports" className="text-primary underline">
                    browse live airport wait times
                  </Link>
                  .
                </CardContent>
              </Card>
            )}

            {SECTIONS.map((section) => {
              const items = guides.filter((g) => g.kind === section.kind);
              if (items.length === 0) return null;
              return (
                <section key={section.kind} className="space-y-3">
                  <h2 className="text-xl font-bold text-foreground">{section.label}</h2>
                  <p className="text-base text-muted-foreground">{section.blurb}</p>
                  <div className="grid gap-3">
                    {items.map((guide) => (
                      <Link
                        key={guide.slug}
                        to={`/guides/${guide.slug}`}
                        className="block rounded-xl border border-border bg-card p-4 shadow-md transition-colors hover:border-primary/40 hover:bg-primary/5 active:scale-95"
                      >
                        <h3 className="text-base font-semibold text-foreground">{guide.title}</h3>
                        <p className="mt-1 text-base text-muted-foreground">{guide.description}</p>
                      </Link>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default GuidesIndex;
